import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PromptLibrary from "@/components/PromptLibrary";
import ReactMarkdown from "react-markdown";
import { Image, Video, Type, Megaphone, FileVideo, BookOpen, LayoutTemplate, Sparkles, Loader2, Trash2, PartyPopper, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, dt, FILES_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ProjectSelect, Badge2, Empty } from "@/components/common";
import Greetings from "@/pages/Greetings";
import { VoiceOver } from "@/components/AiTools";

const TYPES = [["greeting", "Festive Greetings", PartyPopper], ["prompts", "Prompt Library", BookMarked], ["image", "Images", Image], ["video", "Video Storyboards", Video], ["headline", "Headlines", Type], ["ad_copy", "Ad Copy", Megaphone],
  ["script", "Scripts", FileVideo], ["brochure", "Brochures", BookOpen], ["landing_page", "Landing Pages", LayoutTemplate]];

function Pick({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function CreativeStudio() {
  const { can, reload } = useAuth();
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get("tab") || "image");
  const [projectId, setProjectId] = useState("");
  const [f, setF] = useState({ brief: "", platform: "Meta", tone: "Premium" });
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/creatives").then((r) => { setItems(r.data); return r.data; }), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!items.some((i) => i.status === "processing")) return;
    const t = setInterval(async () => { const d = await load(); if (!d.some((i) => i.status === "processing")) reload(); }, 4000);
    return () => clearInterval(t);
  }, [items, load, reload]);

  const gen = async () => {
    setBusy(true);
    try {
      await api.post("/creatives/generate", { type, project_id: projectId, ...f });
      toast.success(type === "image" ? "Generating image (up to 60s)..." : "Generating creative...");
      load(); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/creatives/${id}`); load(); };

  return (
    <div data-testid="creative-studio-page">
      <PageHeader label="Market" title="Creative Studio" subtitle="Generate ad visuals, headlines, copy, scripts, brochures and landing pages. Everything you generate is saved to your asset library.">
        <span className="lx-ai-badge"><Sparkles className="h-3 w-3" />OpenAI GPT + GPT Image 1</span>
      </PageHeader>
      <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2 mb-4">
        {TYPES.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setType(k)} data-testid={`creative-type-${k}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm whitespace-nowrap transition-colors ${type === k ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-800 text-slate-400 hover:border-slate-700"}`}>
            <Icon className="h-4 w-4" />{l}
          </button>
        ))}
      </div>
      {type === "greeting" ? <Greetings onSaved={load} /> : type === "prompts" ? (
        <PromptLibrary onUse={(kind, text) => { setType({ image: "image", greeting: "greeting", video: "video" }[kind] || "ad_copy"); setF({ ...f, brief: text }); }} />
      ) : (
      <div className="lx-card grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="space-y-1.5"><Label className="text-xs text-slate-400">Project</Label><ProjectSelect value={projectId} onChange={setProjectId} className="w-full" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-slate-400">Platform</Label><Pick value={f.platform} onChange={(v) => setF({ ...f, platform: v })} options={["Meta", "Google", "YouTube", "LinkedIn", "WhatsApp", "Print", "Hoarding"]} testid="creative-platform-select" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-slate-400">Tone</Label><Pick value={f.tone} onChange={(v) => setF({ ...f, tone: v })} options={["Premium", "Aspirational", "Family", "Investor", "Festive", "Urgent"]} testid="creative-tone-select" /></div>
        <div className="flex items-end"><Button className="w-full" onClick={gen} disabled={busy || !projectId || !can("marketing", "write")} data-testid="creative-generate-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}Generate</Button></div>
        <div className="md:col-span-4 space-y-1.5"><Label className="text-xs text-slate-400">Creative brief</Label><Textarea rows={2} placeholder="e.g. Sunset view of twin towers with infinity pool, Diwali launch offer" value={f.brief} onChange={(e) => setF({ ...f, brief: e.target.value })} data-testid="creative-brief-input" /></div>
      </div>
      )}
      {!items.length && <Empty title="Asset library is empty">Generate your first creative above.</Empty>}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="creative-library">
        {items.map((c) => (
          <div key={c.id} className="lx-card p-0 overflow-hidden group cursor-pointer" onClick={() => c.status === "completed" && setView(c)} data-testid={`creative-card-${c.id}`}>
            <div className="h-40 bg-slate-900 grid place-items-center overflow-hidden">
              {c.status === "processing" && <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />}
              {c.status === "failed" && <span className="text-xs text-rose-400 px-4">{c.error}</span>}
              {c.status === "completed" && (c.file_url ? <img src={`${FILES_BASE}${c.file_url}`} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                : <p className="text-xs text-slate-400 p-4 line-clamp-6 whitespace-pre-wrap">{c.content?.slice(0, 300)}</p>)}
            </div>
            <div className="p-4 flex items-start justify-between gap-2">
              <div><div className="text-sm font-semibold">{c.title}</div><div className="text-[11px] text-slate-500 font-mono mt-1">{dt(c.created_at)}</div></div>
              <div className="flex items-center gap-1"><Badge2>{c.status}</Badge2>{can("marketing", "write") && <Trash2 className="h-4 w-4 text-slate-500 hover:text-rose-400" onClick={(e) => { e.stopPropagation(); remove(c.id); }} data-testid={`creative-delete-${c.id}`} />}</div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-slate-800" data-testid="creative-view-dialog">
          <DialogHeader><DialogTitle>{view?.title}</DialogTitle></DialogHeader>
          {view?.file_url ? (
            <div className="space-y-3"><img src={`${FILES_BASE}${view.file_url}`} alt="creative" className="w-full rounded-lg" /><a href={`${FILES_BASE}${view.file_url}`} download className="text-sm text-emerald-400 hover:underline">Download image</a></div>
          ) : (
            <div className="prose-lx"><ReactMarkdown>{view?.content || ""}</ReactMarkdown><Button variant="secondary" className="mt-3" onClick={() => { navigator.clipboard.writeText(view.content); toast.success("Copied"); }} data-testid="creative-copy-button">Copy text</Button>{view?.type === "script" && <div className="mt-3"><VoiceOver text={view.content || ""} /></div>}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
